import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from '@core/security/auth.guard';
import { AppComponent } from './app.component';
import { CompanyProfileResolver } from './company-profile/company-profile.resolver';
import { LayoutComponent } from './core/layout/layout.component';
import { MyProfileComponent } from './user/my-profile/my-profile.component';
import { ConfirmPasswordComponent } from './confirm-password/confirm-password.component';
import { SalesrepspasswordchangeComponent } from './user/salesrepspasswordchange/salesrepspasswordchange.component';
import { ValidateUserComponent } from './user/validate-user/validate-user.component';
import { LoginAuthGuard } from '@core/security/login-auth.guard';
import { MobileWebViewReportsComponent } from './reports/mobile-web-view-reports/mobile-web-view-reports.component';
import { MobileWebViewClientReportComponent } from './reports/mobile-web-view-client-report/mobile-web-view-client-report.component';
import { AppForgotPasswordComponent } from './app-forgot-password/app-forgot-password.component';

const routes: Routes = [
  {
    path: 'client-report/:id',
    component: MobileWebViewClientReportComponent,
  },
  {
    path: 'reports/:id',
    component: MobileWebViewReportsComponent,
  },
  {
    path: 'forgot-password',
    component: AppForgotPasswordComponent
  },
  {
    path: '',
    component: AppComponent,
    resolve: { profile: CompanyProfileResolver },
    children: [
      {
        path: 'login',
        canLoad: [LoginAuthGuard],
        loadChildren: () =>
          import('./login/login.module').then((m) => m.LoginModule),
      },
      {
        path: 'generate-new-password',
        component: SalesrepspasswordchangeComponent,
      },
      {
        path: 'validate-user',
        component: ValidateUserComponent,
      },
      {
        path: '',
        component: LayoutComponent,
        children: [
          {
            path: 'confirm-password',
            component: ConfirmPasswordComponent,
          },
          {
            path: 'my-profile',
            component: MyProfileComponent,
            canActivate: [AuthGuard],
          },
          {
            path: 'pos',
            loadChildren: () =>
              import('./pos/pos.module').then((m) => m.PosModule),
          },
          {
            path: '',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./dashboard/dashboard.module').then(
                (m) => m.DashboardModule
              ),
          },
          {
            path: 'pages',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./page/page.module').then((m) => m.PageModule),
          },
          {
            path: 'roles',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./role/role.module').then((m) => m.RoleModule),
          },
          {
            path: 'users',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./user/user.module').then((m) => m.UserModule),
          },
          {
            path: 'login-audit',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./login-audit/login-audit.module').then(
                (m) => m.LoginAuditModule
              ),
          },
          {
            path: 'sessions',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./session/session.module').then((m) => m.SessionModule),
          },
          {
            path: 'emailtemplate',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./email-template/email-template.module').then(
                (m) => m.EmailTemplateModule
              ),
          },
          {
            path: 'send-email',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./email-send/email-send.module').then(
                (m) => m.EmailSendModule
              ),
          },
          {
            path: 'logs',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./n-log/n-log.module').then((m) => m.NLogModule),
          },
          {
            path: 'email-smtp',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./email-smtp-setting/email-smtp-setting.module').then(
                (m) => m.EmailSmtpSettingModule
              ),
          },
          {
            path: 'supplier',
            loadChildren: () =>
              import('./supplier/supplier.module').then(
                (m) => m.SupplierModule
              ),
          },
          {
            path: 'testimonial',
            loadChildren: () =>
              import('./testimonial/testimonial.module').then(
                (m) => m.TestimonialModule
              ),
          },
          {
            path: 'customer',
            loadChildren: () =>
              import('./customer/customer.module').then(
                (m) => m.CustomerModule
              ),
          },
          {
            path: 'contact-us',
            loadChildren: () =>
              import('./contact-us/contact-us.module').then(
                (m) => m.ContactUsModule
              ),
          },
          {
            path: 'notifications',
            loadChildren: () =>
              import('./notification/notification.module').then(
                (m) => m.NotificationModule
              ),
          },
          {
            path: 'reminders',
            loadChildren: () =>
              import('./reminder/reminder.module').then(
                (m) => m.ReminderModule
              ),
          },
          {
            path: 'purchase-order',
            loadChildren: () =>
              import('./purchase-order/purchase-order.module').then(
                (m) => m.PurchaseOrderModule
              ),
          },
          {
            path: 'purchase-order-return',
            loadChildren: () =>
              import(
                './purchase-order-return/purchase-order-return.module'
              ).then((m) => m.PurchaseOrderReturnModule),
          },
          {
            path: 'purchase-order-request',
            loadChildren: () =>
              import(
                './purchase-order-request/purchase-order-request.module'
              ).then((m) => m.PurchaseOrderRequestModule),
          },
          {
            path: 'sales-order',
            loadChildren: () =>
              import('./sales-order/sales-order.module').then(
                (m) => m.SalesOrderModule
              ),
          },
          {
            path: 'sales-order-return',
            loadChildren: () =>
              import('./sale-order-return/sale-order-return.module').then(
                (m) => m.SaleOrderReturnModule
              ),
          },
          {
            path: 'company-profile',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import('./company-profile/company-profile.module').then(
                (m) => m.CompanyProfileModule
              ),
          },
          {
            path: 'commission-setting',
            canLoad: [AuthGuard],
            loadChildren: () =>
              import(
                './commission/app-commission-setting/app-commission-setting.module'
              ).then((m) => m.AppCommissionSettingModule),
          },
          {
            path: 'expense-category',
            loadChildren: () =>
              import('./expense-category/expense-category.module').then(
                (m) => m.ExpenseCategoryModule
              ),
          },
          {
            path: 'expense',
            loadChildren: () =>
              import('./expense/expense.module').then((m) => m.ExpenseModule),
          },
          {
            path: 'inquiry',
            loadChildren: () =>
              import('./inquiry/inquiry.module').then((m) => m.InquiryModule),
          },
          {
            path: 'inquiry-status',
            loadChildren: () =>
              import('./inquiry-status/inquiry-status.module').then(
                (m) => m.InquiryStatusModule
              ),
          },
          {
            path: 'inquiry-source',
            loadChildren: () =>
              import('./inquiry-source/inquiry-source.module').then(
                (m) => m.InquirySourceModule
              ),
          },
          {
            path: 'product-category',
            loadChildren: () =>
              import('./product-category/product-category.module').then(
                (m) => m.ProductCategoryModule
              ),
          },
          {
            path: 'products',
            loadChildren: () =>
              import('./product/product.module').then((m) => m.ProductModule),
          },
          {
            path: 'tax',
            loadChildren: () =>
              import('./tax/tax.module').then((m) => m.TaxModule),
          },
          {
            path: 'brand',
            loadChildren: () =>
              import('./brand/brand.module').then((m) => m.BrandModule),
          },
          {
            path: 'warehouse',
            loadChildren: () =>
              import('./warehouse/warehouse.module').then(
                (m) => m.WarehouseModule
              ),
          },
          {
            path: 'country',
            loadChildren: () =>
              import('./country/country.module').then((m) => m.CountryModule),
          },
          {
            path: 'cities',
            loadChildren: () =>
              import('./city/city.module').then((m) => m.CityModule),
          },
          {
            path: 'state-and-provision',
            loadChildren: () =>
              import('./state-and-provision/state-and-provision.module').then(
                (m) => m.StateAndProvisionModule
              ),
          },
          {
            path: 'markets',
            loadChildren: () =>
              import('./markets/markets.module').then((m) => m.MarketsModule),
          },
          {
            path: 'price-name-settings',
            loadChildren: () =>
              import('./price-name-settings/price-name-settings.module').then(
                (m) => m.PriceNameSettingsModule
              ),
          },
          {
            path: 'terms-and-conditions',
            loadChildren: () =>
              import('./terms-and-conditions/terms-and-conditions.module').then(
                (m) => m.TermsAndConditionsModule
              ),
          },
          {
            path: 'ezorders-app-settings',
            loadChildren: () =>
              import(
                './ezorders-app-settings/ezorders-app-settings.module'
              ).then((m) => m.EzOrdersAppSettingsModule),
          },
          {
            path: 'inventory',
            loadChildren: () =>
              import('./inventory/inventory.module').then(
                (m) => m.InventoryModule
              ),
          },
          {
            path: 'send-file',
            loadChildren: () =>
              import('./send-file/send-file.module').then(
                (m) => m.SendFilesModule
              ),
          },
          {
            path: 'purchase-order-report',
            loadChildren: () =>
              import(
                './reports/purchase-order-report/purchase-order-report.module'
              ).then((m) => m.PurchaseOrderReportModule),
          },
          {
            path: 'sales-order-report',
            loadChildren: () =>
              import(
                './reports/sales-order-report/sales-order-report.module'
              ).then((m) => m.SalesOrderReportModule),
          },
          {
            path: 'purchase-payment-report',
            loadChildren: () =>
              import(
                './reports/purchase-payment-report/purchase-payment-report.module'
              ).then((m) => m.PurchasePaymentReportModule),
          },
          {
            path: 'sales-payment-report',
            loadChildren: () =>
              import(
                './reports/sales-payment-report/sales-payment-report.module'
              ).then((m) => m.SalesPaymentReportModule),
          },
          {
            path: 'sales-purchase-report',
            loadChildren: () =>
              import(
                './reports/sales-purchase-report/sales-purchase-report.module'
              ).then((m) => m.SalesPurchaseReportModule),
          },
          {
            path: 'expense-report',
            loadChildren: () =>
              import('./reports/expense-report/expense-report.module').then(
                (m) => m.ExpenseReportModule
              ),
          },
          {
            path: 'supplier-payment-report',
            loadChildren: () =>
              import(
                './reports/supplier-payments/supplier-payments.module'
              ).then((m) => m.SupplierPaymentsModule),
          },
          {
            path: 'customer-payment-report',
            loadChildren: () =>
              import(
                './reports/customer-payment-report/customer-payment-report.module'
              ).then((m) => m.CustomerPaymentReportModule),
          },
          {
            path: 'product-purchase-report',
            loadChildren: () =>
              import(
                './reports/product-purchase-report/product-purchase-report.module'
              ).then((m) => m.ProductPurchaseReportModule),
          },
          {
            path: 'product-sales-report',
            loadChildren: () =>
              import(
                './reports/product-sales-report/product-sales-report.module'
              ).then((m) => m.ProductSalesReportModule),
          },
          {
            path: 'sales-by-customer',
            loadChildren: () =>
              import(
                './reports/sales-by-customer/sales-by-customer.module'
              ).then((m) => m.SalesByCustomerModule),
          },
          {
            path: 'sales-reps-ranking-report',
            loadChildren: () =>
              import(
                './reports/sales-rep-ranking-report/sales-rep-ranking-report.module'
              ).then((m) => m.SalesRepRankingReportModule),
          },
          {
            path: 'sales-by-category',
            loadChildren: () =>
              import(
                './reports/sales-by-category/sales-by-category.module'
              ).then((m) => m.SalesByCategoryModule),
          },
          {
            path: 'sales-by-product-category-report',
            loadChildren: () =>
              import(
                './reports/sales-by-product-category-report/sales-by-product-category-report.module'
              ).then((m) => m.SalesByProductCategoryReportModule),
          },
          {
            path: 'sales-over-time-report',
            loadChildren: () =>
              import('./reports/sales-over-time/sales-over-time.module').then(
                (m) => m.SalesOverTimeModule
              ),
          },
          {
            path: 'sales-by-category-breakdown',
            loadChildren: () =>
              import(
                './reports/sales-by-category-with-breakdown-report/sales-by-category-with-breakdown-report.module'
              ).then((m) => m.SalesByCategoryWithBreakdownReportModule),
          },
          {
            path: 'best-seller-product',
            loadChildren: () =>
              import(
                './reports/best-seller-product/best-seller-product.module'
              ).then((m) => m.BestSellerProductModule),
          },
          {
            path: 'customer-inventory-report',
            loadChildren: () =>
              import(
                './reports/customer-inventory-report/customer-inventory-report.module'
              ).then((m) => m.CustomerInventoryReportModule),
          },
          {
            path: 'sales-by-report-category',
            loadChildren: () =>
              import(
                './reports/sales-by-report-category/sales-by-report-category.module'
              ).then((m) => m.SalesByReportCategoryModule),
          },
          {
            path: 'product-in-stock',
            loadChildren: () =>
              import('./reports/product-in-stock/product-in-stock.module').then(
                (m) => m.ProductInStockModule
              ),
          },
          {
            path: 'best-seller-category',
            loadChildren: () =>
              import(
                './reports/best-seller-category/best-seller-category.module'
              ).then((m) => m.BestSellerCategoryModule),
          },
          {
            path: 'sales-history-by-sales-representative',
            loadChildren: () =>
              import(
                './reports/sales-history-by-sales-representative/sales-history-by-sales-representative.module'
              ).then((m) => m.SalesHistoryBySalesRepresentativeModule),
          },
          {
            path: 'sales-rep-summary',
            loadChildren: () =>
              import(
                './reports/sales-rep-summary-report/sales-rep-summary-report.module'
              ).then((m) => m.SalesRepSummaryReportModule),
          },
          {
            path: 'customer-base-report',
            loadChildren: () =>
              import(
                './reports/customer-base-report/customer-base-report.module'
              ).then((m) => m.CustomerBaseReportModule),
          },
          {
            path: 'stock-report',
            loadChildren: () =>
              import('./reports/stock-report/stock-report.module').then(
                (m) => m.StockReportModule
              ),
          },
          {
            path: 'profit-loss-report',
            loadChildren: () =>
              import(
                './reports/profit-loss-report/profit-loss-report.module'
              ).then((m) => m.ProfitLossReportModule),
          },
          {
            path: 'unitConversation',
            loadChildren: () =>
              import('./unit-conversation/unit-conversation.module').then(
                (m) => m.UnitConversationModule
              ),
          },
          {
            path: 'warehouse-report',
            loadChildren: () =>
              import(
                './reports/warehouse-stock-report/warehouse-stock-report.module'
              ).then((m) => m.WarehouseStockReportModule),
          },
          {
            path: 'product-warehouse-report',
            loadChildren: () =>
              import(
                './reports/product-warehouse-report/product-warehouse-report.module'
              ).then((m) => m.ProductWarehouseReportModule),
          },
          {
            path: 'product-frame',
            loadChildren: () =>
              import('./frame/frame.module').then((m) => m.FrameModule),
          },
          {
            path: 'product-type',
            loadChildren: () =>
              import('./product-type/product-type.module').then(
                (m) => m.ProductTypeModule
              ),
          },
          {
            path: 'customer-special-pricing',
            loadChildren: () =>
              import(
                './customer-special-pricing/customer-special-pricing.module'
              ).then((m) => m.CustomerSpecialPricingModule),
          },
          {
            path: 'product-volume-pricing',
            loadChildren: () =>
              import(
                './product-volume-pricing/product-volume-pricing.module'
              ).then((m) => m.ProductVolumePricingModule),
          },
          {
            path: 'related-products',
            loadChildren: () =>
              import('./related-products/related-products.module').then(
                (m) => m.RelatedProductsModule
              ),
          },
          {
            path: 'tags',
            loadChildren: () =>
              import('./tags/tags.module').then((m) => m.TagsModule),
          },
          {
            path: 'salesreps',
            loadChildren: () =>
              import('./sales-reps/sales-reps.module').then(
                (m) => m.SalesRepsModule
              ),
          },
          {
            path: 'items',
            loadChildren: () =>
              import('./browsecategory/browsecategory.module').then(
                (m) => m.BrowsecategoryModule
              ),
          },
          {
            path: 'best-selling-product',
            loadChildren: () =>
              import('./best-selling-product/best-selling-product.module').then(
                (m) => m.BestSellingProductModule
              ),
          },
          {
            path: 'best-selling-category',
            loadChildren: () =>
              import(
                './best-selling-category/best-selling-category.module'
              ).then((m) => m.BestSellingCategoryModule),
          },
          {
            path: 'manage-category',
            loadChildren: () =>
              import('./manage-categories/manage-categories.module').then(
                (m) => m.ManageCategoriesModule
              ),
          },
          {
            path: 'territory',
            loadChildren: () =>
              import('./territories/territories.module').then(
                (m) => m.TerritoriesModule
              ),
          },
          {
            path: 'bookmarks',
            loadChildren: () =>
              import('./bookmarks/bookmarks.module').then(
                (m) => m.BookmarksModule
              ),
          },
          {
            path: 'discounts',
            loadChildren: () =>
              import('./discounts/discounts.module').then(
                (m) => m.DiscountsModule
              ),
          },
          {
            path: 'shipping-cost',
            loadChildren: () =>
              import('./shipping-cost-list/shipping-cost.module').then(
                (m) => m.ShippingCostListModule
              ),
          },
          {
            path: 'up-charge',
            loadChildren: () =>
              import('./up-charge/up-charge.module').then(
                (m) => m.UpChargeModule
              ),
          },
          {
            path: 'labels',
            loadChildren: () =>
              import('./labels/labels.module').then((m) => m.LabelsModule),
          },
          {
            path: 'import-data',
            loadChildren: () =>
              import('./import-data/import-data.module').then(
                (m) => m.ImportDataModule
              ),
          },
          {
            path: '**',
            redirectTo: '/',
          },
        ],
      },
    ],
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
      relativeLinkResolution: 'legacy',
    }),
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
